/**
 * Wire types for `chatbot-service` (`/api/v1/chat`).
 *
 * These mirror the service's responses exactly — no renaming, no adapters.
 * Django's `ChatBotType` (`message`/`is_user`/`metadata`) and `ChatListItem`
 * (embedded `vulnerability`) are gone: the service holds no scan-domain
 * knowledge and cannot serve a finding summary. Read `subject_id` and fetch
 * the finding from Django where a title or severity is needed.
 *
 * See docs/superpowers/specs/2026-08-09-chatbot-service-migration-contract.md
 * — the `§` references below point at its numbered sections.
 */

/** Every collection endpoint returns exactly this envelope. No total, no cursor. */
export interface ItemsResponse<T> {
  items: T[]
}

/**
 * Where a gated call will actually act, resolved by the SERVICE from the
 * call's `session_id` — never from the model's own arguments.
 *
 * That distinction is the whole point of the field. With several sessions
 * live, `args` says only which id the model named; this says what that id
 * really is, so the person approving a command can see the machine before
 * they approve it rather than an opaque identifier.
 *
 * A discriminated union because the two tools it can describe are not the
 * same kind of exposure: `start_patch_session` opens an interactive SSH
 * session on `host:port` as `username`, while a connectivity check merely
 * probes `ip`'s `ports` from the sandbox and returns. Collapsing both into
 * one `{host, username, port}` shape (the pre-sandbox wire contract) would
 * have a verify target render `undefined@host:undefined` — or worse, invite
 * `ApprovalCard` to mislabel a live outbound check as an SSH session. `kind`
 * is what `ApprovalCard.renderTarget` switches on to pick the right copy.
 */
export type ApprovalTarget =
  | { kind: 'ssh'; host: string; username: string; port: number }
  | { kind: 'verify'; host: string; ip: string; ports: number[] }

/**
 * One gated tool call as persisted on the assistant message.
 *
 * NOTE the key names: the live `approval_required` stream event calls these
 * `id` and `name`. Same values, different keys — normalise via
 * `approvalState.ts` so `ApprovalCard` only ever sees this shape.
 */
export interface ApprovalRequestCall {
  tool_call_id: string
  tool_name: string
  /**
   * Already parsed by the service — a real object, not a JSON string. (OpenAI's
   * own tool-call convention puts a string in `arguments`; this is not that.)
   */
  args: Record<string, unknown>
  /** null until the user decides. */
  decision: 'approved' | 'denied' | null
  /** The reason given on a denial; null otherwise. Survives a reload. */
  denial_message: string | null
  /**
   * Optional because a block written before the service resolved targets has
   * no such key, and because a call that names no session has no target.
   */
  target?: ApprovalTarget | null
}

export interface ApprovalRequestBlock {
  type: 'approval_request'
  /**
   * Flips to `resolved` only once the resume has *executed* the approved work,
   * not when the decision is recorded. A card can sit `open` with every
   * decision filled in while the tools run.
   */
  state: 'open' | 'resolved'
  calls: ApprovalRequestCall[]
}

/**
 * A downloadable file the agent's sandbox produced, persisted alongside the
 * assistant message that made it. `download_path` is an already-scoped API
 * path (`/api/v1/chat/<chatId>/artifacts/<id>`) — the frontend does not build
 * it from `artifact_id`, it renders it verbatim against the API origin, same
 * as every other authenticated call: cookies, not a bearer token.
 */
export interface ArtifactBlock {
  type: 'artifact'
  artifact_id: string
  filename: string
  content_type: string
  size_bytes: number
  download_path: string
}

/**
 * Any block type the frontend does not model yet. It survives a round trip
 * untouched, but you cannot index a `MessageBlock` directly to reach these
 * extra keys — `ApprovalRequestBlock` is an interface and so contributes no
 * implicit index signature, making `block[key]` a TS7053 error on the union.
 * Narrow with `isApprovalRequestBlock` first and read them in the `else`.
 */
export interface UnknownBlock {
  type: string
  [key: string]: unknown
}

export type MessageBlock = ApprovalRequestBlock | ArtifactBlock | UnknownBlock

/**
 * The inner block envelope. The service carries Django's *persisted* shape
 * through verbatim — `{v, blocks}` is what sat in the `metadata` column, and
 * §6 keeps it deliberately unchanged. Note this was never modelled on the
 * frontend before: the old `MetaData` type was `{pattern, data}`, something
 * else entirely.
 */
export interface MessageBlocks {
  v: number
  blocks: MessageBlock[]
}

export interface ChatMessage {
  id: string
  chat_id: string
  /**
   * Closed deliberately, unlike `subject_type` below: §6 states the service
   * persists these two values and no others. There is no `system` or `tool`
   * role on the wire — those stay inside the service.
   */
  role: 'user' | 'assistant'
  content: string
  blocks?: MessageBlocks
  created_at: string
}

export interface Chat {
  id: string
  organization_id: string
  /**
   * The owner. Compare against `me.id` — both are strings. See §9 of the
   * contract doc named in the file header.
   */
  user_id: string
  /**
   * Opaque to the frontend. `"finding"` today; `"asset"`/`"scan"` may follow
   * with no change here. Do not `switch` on it except for display copy.
   */
  subject_type: string | null
  /**
   * Carries exactly the value Django's `finding_id` used to carry, so it is
   * the key you fetch the finding with when a title or severity is needed.
   */
  subject_id: string | null
  /**
   * Derived once from the first non-empty user message and never rewritten.
   * `null` for a chat that never got a message, and for every chat that
   * predates the service — there is no backfill. Always render a fallback.
   */
  title: string | null
  /**
   * Tri-state thumbs feedback: `true` helpful, `false` not, `null` when the
   * user has not answered yet. Unlike Django this is readable, so the feedback
   * control can render from it instead of tracking the choice locally.
   */
  is_helpful: boolean | null
  created_at: string
  updated_at: string
}

export function isApprovalRequestBlock(block: MessageBlock): block is ApprovalRequestBlock {
  return block.type === 'approval_request' && Array.isArray((block as ApprovalRequestBlock).calls)
}

/**
 * Narrows a `MessageBlock` to `ArtifactBlock`. Checks every field the render
 * site reads without a further guard of its own (`ChatInner` filters with
 * this and then trusts the shape) — `ApprovalCard.renderTarget` re-checks its
 * own wire boundary because a throw there costs the whole message list, but
 * an artifact block that fails this narrows to nothing rendered, which is
 * exactly as safe with a lighter guard.
 */
export function isArtifactBlock(block: MessageBlock): block is ArtifactBlock {
  const candidate = block as ArtifactBlock
  return (
    block.type === 'artifact' &&
    typeof candidate.artifact_id === 'string' &&
    typeof candidate.filename === 'string' &&
    typeof candidate.content_type === 'string' &&
    typeof candidate.size_bytes === 'number' &&
    typeof candidate.download_path === 'string'
  )
}
